import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Lock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      toast.success('Contraseña actualizada');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al restablecer contraseña');
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="glass-card p-12 w-full max-w-md text-center">
          <XCircle className="w-16 h-16 text-error mx-auto mb-6" />
          <h2 className="font-heading font-bold text-2xl mb-4">Enlace Inválido</h2>
          <p className="text-secondary mb-6">
            El enlace para restablecer contraseña no es válido o ha expirado.
          </p>
          <Link to="/forgot-password">
            <Button className="bg-white text-black hover:bg-gray-200 rounded-full">
              Solicitar nuevo enlace
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-hero-glow opacity-30 blur-3xl" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-12 w-full max-w-md relative z-10"
      >
        <div className="flex items-center justify-center space-x-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-ai-glow to-brand-meta" />
          <span className="font-heading font-bold text-xl">AdPulse AI</span>
        </div>

        {success ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-4">¡Contraseña Actualizada!</h2>
            <p className="text-secondary mb-6">
              Tu contraseña ha sido restablecida correctamente.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-white text-black hover:bg-gray-200 rounded-full h-12"
            >
              Ir al Login
            </Button>
          </div>
        ) : error ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-error/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-error" />
            </div>
            <h2 className="font-heading font-bold text-2xl mb-4">Error</h2>
            <p className="text-secondary mb-6">{error}</p>
            <Link to="/forgot-password">
              <Button className="w-full bg-white text-black hover:bg-gray-200 rounded-full h-12">
                Solicitar nuevo enlace
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-heading font-bold text-2xl text-center mb-2">Nueva Contraseña</h2>
            <p className="text-secondary text-center text-sm mb-8">
              Ingresa tu nueva contraseña
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="font-mono text-xs uppercase tracking-widest text-secondary">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/20 rounded-lg h-12 pl-12"
                    required
                    minLength={6}
                    data-testid="reset-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="font-mono text-xs uppercase tracking-widest text-secondary">
                  Confirmar Contraseña
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/20 rounded-lg h-12 pl-12"
                    required
                    data-testid="reset-confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-ai-glow to-brand-meta text-white hover:opacity-90 rounded-full h-12"
                disabled={loading}
                data-testid="reset-submit-button"
              >
                {loading ? 'Guardando...' : 'Restablecer Contraseña'}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
